/**
 * `storeHome.*` — the ERP composer's read/write surface (spec 42 R8, R9).
 *
 * Read returns whatever the composer should open with: the latest staged
 * version if one exists, else the published one, else the default composition
 * — flagged so the screen can say "Default — not yet published" honestly.
 *
 * Write takes the whole ordered section list. There is no per-section
 * endpoint and no reorder endpoint: R10 requires the full list precisely so a
 * concurrent edit cannot interleave into a half-applied order.
 */
import { z } from "zod";
import { uuid } from "../_common.js";
import { sectionsSchema, sectionSchema, storeThemeSchema } from "../store-home.js";

/** Where the composer's current view came from — drives the status pill. */
export const homeVersionOrigin = z.enum(["draft", "published", "default"]);
export type HomeVersionOrigin = z.infer<typeof homeVersionOrigin>;

export const getHomeInput = z.object({
  /**
   * Which named appearance to open (spec 50 R5). Omitted → the pre-spec-50
   * behaviour: the store's newest staged version, whichever appearance it
   * belongs to. The editor always passes one; the fallback exists so a caller
   * that just wants "what is this store's current layout" still has an answer.
   */
  appearanceId: uuid.optional(),
});

export const getHomeOutput = z.object({
  /** Null when the store has never staged a version. */
  versionId: uuid.nullable(),
  origin: homeVersionOrigin,
  /** The appearance this composition belongs to — null on a pre-spec-50 store. */
  appearanceId: uuid.nullable(),
  /** Its name, for the editor's toolbar. Null when there is no appearance yet. */
  appearanceName: z.string().nullable(),
  sections: z.array(sectionSchema),
  /** Per-store appearance for this version — defaults when never themed. */
  theme: storeThemeSchema,
  /** The currently live version, for the composer's "what changes" diff. */
  publishedVersionId: uuid.nullable(),
  /**
   * What buyers see right now. The composer diffs the staged list against this
   * — without it the dialog can only compare against nothing and reports every
   * section as newly added on every publish.
   */
  publishedSections: z.array(sectionSchema),
  /** Store profile context the section forms need (collection picker, etc.). */
  collections: z.array(z.object({ slug: z.string(), name: z.string() })),
  /** Identity + profile the preview needs to render the real components. */
  storeName: z.string(),
  storeSlug: z.string(),
  /** Contact number — the preview needs it or a WhatsApp CTA never appears there. */
  phone: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  profile: z.object({
    tagline: z.string().nullable(),
    establishedYear: z.number().int().nullable(),
    teamSize: z.string().nullable(),
    monthlyCapacity: z.string().nullable(),
    city: z.string().nullable(),
    about: z.string().nullable(),
  }),
});

export const saveHomeInput = z.object({
  /**
   * Which named appearance this save belongs to (spec 50 R5). **Required** — a
   * version with no appearance is unreachable from the library screen, so
   * allowing the field to be omitted would quietly mint orphans.
   */
  appearanceId: uuid,
  /** Validated by the shared registry — caps and singletons included (R10). */
  sections: sectionsSchema,
  /** Appearance staged with this version; omitted → server stages the default. */
  theme: storeThemeSchema.optional(),
});

export const saveHomeOutput = z.object({ versionId: uuid });

// ── publish / rollback (spec 42 R4, R11) ───────────────────────

export const publishHomeInput = z.object({
  /** Which staged version goes live. Rollback passes an older id. */
  versionId: uuid,
});

export const publishHomeOutput = z.object({
  versionId: uuid,
  /** Null on a store's first publish. */
  previousVersionId: uuid.nullable(),
});

export const listHomeVersionsInput = z.object({
  /**
   * Scope the history to one appearance (spec 50 R5). Omitted → every version
   * in the store, the pre-spec-50 behaviour.
   *
   * The editor always passes it: since a store holds several appearances, an
   * unscoped rollback picker would offer to publish a *different* design's
   * version from inside this one's canvas.
   */
  appearanceId: uuid.optional(),
  /**
   * Include each version's full composition so the picker can draw a preview.
   *
   * Off by default and requested only when the picker opens. The editor needs
   * this list on every load just to label the toolbar button, and shipping a
   * dozen whole compositions on every load — to populate a control most
   * sessions never open — is waste.
   */
  withContent: z.boolean().optional(),
});

export const listHomeVersionsOutput = z.object({
  items: z.array(
    z.object({
      id: uuid,
      createdAt: z.string(),
      sectionCount: z.number().int(),
      isPublished: z.boolean(),
      /** Present only under `withContent` — the preview thumbnail's input. */
      sections: z.array(z.unknown()).optional(),
      theme: z.unknown().optional(),
    }),
  ),
});

// ── store profile (spec 28 R1) ─────────────────────────────────

/** Every field is sent every time; `null` clears it. */
export const updateStoreProfileInput = z.object({
  coverImageUrl: z.string().trim().max(2048).nullable(),
  tagline: z.string().trim().max(120).nullable(),
  about: z.string().trim().max(1200).nullable(),
  /** Upper bound is the domain's — a CHECK cannot call the clock. */
  establishedYear: z.number().int().min(1900).nullable(),
  teamSize: z.string().trim().max(32).nullable(),
  monthlyCapacity: z.string().trim().max(32).nullable(),
});

export const updateStoreProfileOutput = z.object({ ok: z.literal(true) });
